import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DEVELOPMENT_LEVELS, developmentLevelLabel } from '../../shared/developmentLevels';
import { classPulseDate } from './classPulseMetrics';
import { teacherTypography } from '../../design/typography';
import { TeacherSectionHeader, TeacherAssessmentHistoryRow } from '../../design/TeacherComponents';
import { unifiedUI } from '../../design/unified';

export default function DevelopmentAssessmentDistribution({ summary, onSelectStudent }) {
  const [expanded, setExpanded] = useState(false);
  if (!summary.exams.length) return null;
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: '14px', mb: 2.5 }}>
      <TeacherSectionHeader title="Gelişim Düzeyi" />
      <Typography variant="body2" sx={{ ...teacherTypography.supportingText, mt: 0.5, mb: 2 }}>
        {summary.resultCount} sonuç kaydı · {summary.studentCount} farklı öğrenci. Düzeyler puan ortalamalarına dahil edilmez.
      </Typography>
      {!expanded && <Box>
        {summary.exams.map(exam => <TeacherAssessmentHistoryRow key={exam.id}
          title={exam.title.startsWith(exam.subject) ? exam.title : `${exam.subject} — ${exam.title}`} dateLabel={classPulseDate(exam.date)}
          metadata={`${exam.evaluated} düzey sonucu · ${exam.pending} bekliyor · ${exam.absent} katılmadı`} />)}
      </Box>}
      <Box component="details" data-teacher-development-details
        onToggle={event => setExpanded(event.currentTarget.open)}
        sx={{ mt: 1, '& > summary': { cursor: 'pointer', minHeight: 44, display: 'list-item',
          alignContent: 'center', color: unifiedUI.purple, fontWeight: 600, fontSize: 13, py: 1, ml: 2,
          '&:focus-visible': { outline: '3px solid #5336AA', outlineOffset: 2 } } }}>
        <summary>{expanded ? 'Ayrıntıları gizle' : 'Ayrıntıları göster'} · {summary.exams.length} değerlendirme</summary>
      <Stack spacing={2} sx={{ mt: 1 }}>
        {summary.exams.map(exam => (
          <Paper component="section" aria-label={`${exam.subject} — ${exam.title} düzey dağılımı`}
            key={exam.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, minWidth: 0 }}>
            <Typography component="h3" variant="subtitle2" sx={{ ...teacherTypography.cardTitle, overflowWrap: 'anywhere' }}>
              {exam.subject} — {exam.title}
            </Typography>
            <Typography variant="caption" sx={teacherTypography.metadata}>{classPulseDate(exam.date)}</Typography>
            <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} sx={{ my: 1 }}>
              <Chip label={`${exam.evaluated} değerlendirildi`} size="small" variant="outlined" />
              <Chip label={`${exam.pending} bekliyor`} size="small" variant="outlined" />
              <Chip label={`${exam.absent} katılmadı`} size="small" variant="outlined" />
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,minmax(0,1fr))', sm: 'repeat(4,minmax(0,1fr))' }, gap: 1 }}>
              {DEVELOPMENT_LEVELS.map(level => (
                <Box key={level.code} sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <Typography variant="body2">{level.label}</Typography>
                  <Typography sx={teacherTypography.kpiValue}>{exam.distribution[level.code]}</Typography>
                </Box>
              ))}
            </Box>
            <Stack sx={{ mt: 1 }}>
              {exam.students.map(student => (
                <Button key={student.id} color="inherit" onClick={() => onSelectStudent(student.id)}
                  aria-label={`${student.name} detayını gör`}
                  sx={{ minHeight: 44, minWidth: 0, textTransform: 'none', justifyContent: 'space-between', gap: 1,
                    textAlign: 'left', '&.Mui-focusVisible': { outline: '3px solid #302479', outlineOffset: 1 } }}>
                  <Typography variant="body2" sx={{ ...teacherTypography.studentName, overflowWrap: 'anywhere', minWidth: 0 }}>{student.name}</Typography>
                  <Typography variant="body2" sx={{ flexShrink: 0 }}>
                    {developmentLevelLabel(student.developmentLevel) || (student.status === 'absent' ? 'Katılmadı' : 'Sonuç bekleniyor')}
                  </Typography>
                </Button>
              ))}
            </Stack>
          </Paper>
        ))}
      </Stack>
      </Box>
    </Paper>
  );
}
